import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogService, OfferingResponse } from '../../features/catalog/catalog.service';

interface OfferingOption {
  id: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-offering-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OfferingSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t(placeholderKey)"
      [showClear]="showClear"
      [filter]="showSearch"
      filterBy="label,id"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      [appendTo]="appendTo" />
  `,
  styles: [`.selector { width: 100%; }`]
})
export class OfferingSelectorComponent implements ControlValueAccessor {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'catalog.selectors.offering.placeholder';
  @Input() showSearch = true;
  @Input() showClear = true;
  @Input() appendTo: 'body' | 'self' = 'body';

  readonly loading = signal(false);
  readonly options = signal<OfferingOption[]>([]);
  readonly value = signal<string | null>(null);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value?.trim() || null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private load(): void {
    this.loading.set(true);
    this.service.listOfferings().subscribe({
      next: rows => {
        this.options.set(rows.map(row => this.toOption(row)));
        this.loading.set(false);
      },
      error: () => {
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private toOption(row: OfferingResponse): OfferingOption {
    return { id: row.id, label: `${row.code} - ${row.title}` };
  }
}
